import '../styles/themes/global.scss'

import { useContext } from 'react'

import { DarkModePreference } from './darkModeTypes'
import { ThemeContext } from './Theme'
import { themeSupportsDarkMode } from './themeCapabilities'
import { DarkMode, DesignLanguage, Theme, ThemeProps } from './types'

export const useTheme = (): ThemeProps => {
  return useContext(ThemeContext)
}

export const useThemeClass = (): string => {
  const {
    theme: { selected },
  } = useTheme()

  return `theme${selected}`
}

export const useThemePerClass = (classNames: { [key: string]: string }): { [key: string]: string } => {
  const {
    theme: { selected },
  } = useTheme()

  return Object.entries(classNames).reduce((accumulator: { [key: string]: string }, [key, value]) => {
    const strippedKey = key.replace(`${selected}_`, '').trim()

    if (typeof accumulator[strippedKey] === 'undefined') {
      accumulator[strippedKey] = value
    } else {
      accumulator[strippedKey] += ` ${value}`
    }

    return accumulator
  }, {})
}

export const useDesignLanguagePerClass = (classNames: { [key: string]: string }): { [key: string]: string } => {
  const {
    theme: { designLanguage },
  } = useTheme()

  return Object.entries(classNames).reduce((accumulator: { [key: string]: string }, [key, value]) => {
    const strippedKey = key.replace(`${designLanguage}_`, '').trim()

    if (typeof accumulator[strippedKey] === 'undefined') {
      accumulator[strippedKey] = value
    } else {
      accumulator[strippedKey] += ` ${value}`
    }

    return accumulator
  }, {})
}

/**
 * @deprecated use `useIsTheme` instead
 */
export const useIsAPTheme = (): boolean => {
  const {
    theme: { selected },
  } = useTheme()

  return selected === Theme.AP
}

/**
 * @deprecated use `useIsTheme` instead
 */
export const useIsCashTheme = (): boolean => {
  const {
    theme: { selected },
  } = useTheme()

  return selected === Theme.Cash
}

export const useGetSelectedTheme = (): Theme => {
  const {
    theme: { selected },
  } = useTheme()

  return selected
}

export const useIsTheme = (theme: Theme): theme is Theme => {
  const {
    theme: { selected },
  } = useTheme()

  return selected === theme
}

export const useIsDarkMode = (): boolean => {
  const {
    theme: { isDarkModeActive, selected },
  } = useTheme()

  return isDarkModeActive && themeSupportsDarkMode(selected)
}

export const useIsOsDarkMode = (): boolean => {
  const {
    theme: { isDarkModeActive },
  } = useTheme()

  return isDarkModeActive
}

export const useIsDesignLanguage = (designLanguage: DesignLanguage): designLanguage is DesignLanguage => {
  const {
    theme: { designLanguage: currentLayout },
  } = useTheme()

  return currentLayout === designLanguage
}

export const useGetSelectedDesignLanguage = (): DesignLanguage => {
  const {
    theme: { designLanguage },
  } = useTheme()

  return designLanguage
}

export const useDarkMode = (): { isDarkMode: boolean; setDarkMode: (darkMode: DarkMode) => void } => {
  const {
    theme: { setDarkMode },
  } = useTheme()

  return {
    isDarkMode: useIsDarkMode(),
    setDarkMode,
  }
}

export const useDarkModePreference = (): {
  isDarkMode: boolean
  setDarkMode: () => void
  setLightMode: () => void
  setSystemMode: () => void
  setPreference: (preference: DarkModePreference) => void
} => {
  const {
    theme: { setDarkMode: setThemeDarkMode },
  } = useTheme()

  return {
    isDarkMode: useIsDarkMode(),
    setDarkMode: () => setThemeDarkMode(DarkMode.Dark),
    setLightMode: () => setThemeDarkMode(DarkMode.Light),
    setSystemMode: () => setThemeDarkMode(DarkMode.System),
    setPreference: (pref: DarkModePreference) => {
      if (pref === 'dark') {
        setThemeDarkMode(DarkMode.Dark)
      } else if (pref === 'light') {
        setThemeDarkMode(DarkMode.Light)
      } else {
        setThemeDarkMode(DarkMode.System)
      }
    },
  }
}
